import React from 'react';
import ReportSectionHeader from './ReportSectionHeader';
import EmptySection from './EmptySection';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { ShieldCheckIcon } from '../../../components/SiteProofLogo';

const PRIORITY_COLORS = {
  high: 'bg-red-100 text-red-700',
  medium: 'bg-yellow-100 text-yellow-700',
  low: 'bg-green-100 text-green-700',
};

const STATUS_COLORS = {
  open: 'bg-orange-100 text-orange-700',
  'in-progress': 'bg-blue-100 text-blue-700',
  resolved: 'bg-green-100 text-green-700',
  completed: 'bg-green-100 text-green-700',
  pending: 'bg-gray-100 text-gray-700',
  valid: 'bg-green-100 text-green-700',
  'expiring-soon': 'bg-yellow-100 text-yellow-700',
  expired: 'bg-red-100 text-red-700',
};

const formatTime = (isoString) => {
  if (!isoString) return '—';
  try {
    return new Date(isoString)?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch { return '—'; }
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr)?.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return dateStr; }
};

const ReportView = ({ reportData, onBack, onExportPDF, siteVisibility, userRole, pdfExporting }) => {
  const {
    site,
    date,
    generatedBy,
    generatedAt,
    tasks,
    photos,
    snags,
    incidents,
    nearMisses,
    documents,
    diary,
  } = reportData || {};

  const showSnags = userRole !== 'client' || siteVisibility?.canViewSnags !== false;
  const showPhotos = userRole !== 'client' || siteVisibility?.canViewPhotos !== false;
  const showDocuments = userRole !== 'client' || siteVisibility?.canViewDocuments !== false;

  const allIncidents = [...(incidents || []), ...(nearMisses || [])];

  return (
    <div className="space-y-6">
      {/* Action bar — screen only, not captured in PDF */}
      <div className="bg-card border border-border rounded-lg shadow-elevation-2 p-6 print:shadow-none print:border-0 print:p-0">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Icon name="Building2" size={20} color="var(--color-accent)" />
              <h1 className="text-xl font-bold text-foreground">{site?.site_name}</h1>
            </div>
            <p className="text-sm text-muted-foreground">Daily Site Report — {formatDate(date)}</p>
          </div>
          <div className="flex gap-2 print:hidden">
            <Button variant="outline" size="sm" iconName="ArrowLeft" onClick={onBack} disabled={pdfExporting}>
              New Report
            </Button>
            <Button
              variant="primary"
              size="sm"
              iconName={pdfExporting ? 'Loader' : 'Download'}
              onClick={onExportPDF}
              disabled={pdfExporting}
            >
              {pdfExporting ? 'Generating PDF…' : 'Download PDF'}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-border">
          <div>
            <p className="text-xs text-muted-foreground mb-0.5">Report Date</p>
            <p className="text-sm font-medium text-foreground">{formatDate(date)}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-0.5">Generated By</p>
            <p className="text-sm font-medium text-foreground">{generatedBy || '—'}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-0.5">Generated At</p>
            <p className="text-sm font-medium text-foreground">{generatedAt ? new Date(generatedAt)?.toLocaleString() : '—'}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-0.5">Site Reference</p>
            <p className="text-sm font-medium text-foreground">{site?.site_reference || '—'}</p>
          </div>
        </div>
      </div>

      {/* PDF capture target — wraps all report content sections */}
      <div id="daily-site-report-content" style={{ backgroundColor: '#ffffff' }}>
        {/* PDF-visible report header */}
        <div className="mb-6 pb-4 border-b-2 border-gray-300">
          <div className="flex items-center justify-between mb-2">
            {/* Logo lockup */}
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
              <ShieldCheckIcon size={32} />
              <span style={{ fontWeight: 700, fontSize: '1.25rem', color: '#111827', letterSpacing: '-0.025em' }}>
                SiteProof
              </span>
            </span>
            <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>Daily Site Report</span>
          </div>
          <h2 className="text-xl font-bold text-gray-900">{site?.site_name}</h2>
          <p className="text-sm text-gray-500 mt-0.5">Daily Site Report — {formatDate(date)}</p>
          <div className="flex flex-wrap gap-6 mt-2 text-xs text-gray-600">
            <span><strong>Report Date:</strong> {formatDate(date)}</span>
            <span><strong>Generated By:</strong> {generatedBy || '—'}</span>
            <span><strong>Generated At:</strong> {generatedAt ? new Date(generatedAt)?.toLocaleString() : '—'}</span>
            {site?.site_reference && <span><strong>Site Ref:</strong> {site?.site_reference}</span>}
          </div>
        </div>

        {/* Tasks Section */}
        <div className="bg-white border border-gray-200 rounded-lg p-5 mb-4">
          <ReportSectionHeader title="Tasks Completed" icon="CheckSquare" count={tasks?.length || 0} />
          {!tasks?.length ? (
            <EmptySection message="No tasks completed on this date." />
          ) : (
            <div className="space-y-2">
              {tasks?.map((task) => (
                <div key={task?.id} className="flex items-start justify-between py-2 border-b border-gray-100 last:border-0">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{task?.title}</p>
                    {task?.assigned_user?.full_name && (
                      <p className="text-xs text-gray-500 mt-0.5">
                        Assigned to: {task?.assigned_user?.full_name}
                      </p>
                    )}
                    {task?.work_carried_out_by && (
                      <p className="text-xs text-gray-500 mt-0.5">
                        Work carried out by: {task?.work_carried_out_by}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 ml-4 flex-shrink-0">
                    <span className="text-xs text-gray-500">{formatTime(task?.completed_at)}</span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                      Completed
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Photos Section */}
        {showPhotos && (
          <div className="bg-white border border-gray-200 rounded-lg p-5 mb-4">
            <ReportSectionHeader title="Photos Uploaded" icon="Camera" count={photos?.length || 0} />
            {!photos?.length ? (
              <EmptySection message="No photos uploaded on this date." />
            ) : (
              <div>
                <p className="text-sm text-gray-500 mb-3">{photos?.length} photo{photos?.length !== 1 ? 's' : ''} uploaded</p>
                <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                  {photos?.slice(0, 24)?.map((photo, idx) => (
                    <div
                      key={photo?.id || idx}
                      className="aspect-square bg-gray-100 rounded-md overflow-hidden border border-gray-200"
                    >
                      {photo?.signedUrl || photo?.url ? (
                        <img
                          src={photo?.signedUrl || photo?.url}
                          alt={`Site photo ${idx + 1}`}
                          className="w-full h-full object-cover"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Icon name="Image" size={16} className="text-gray-400" />
                        </div>
                      )}
                    </div>
                  ))}
                  {photos?.length > 24 && (
                    <div className="aspect-square bg-gray-100 rounded-md border border-gray-200 flex items-center justify-center">
                      <span className="text-xs text-gray-500 font-medium">+{photos?.length - 24}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Snags Section */}
        {showSnags && (
          <div className="bg-white border border-gray-200 rounded-lg p-5 mb-4">
            <ReportSectionHeader title="Snags Raised" icon="AlertCircle" count={snags?.length || 0} />
            {!snags?.length ? (
              <EmptySection message="No snags raised on this date." />
            ) : (
              <div className="space-y-2">
                {snags?.map((snag) => (
                  <div key={snag?.id} className="flex items-start justify-between py-2 border-b border-gray-100 last:border-0">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{snag?.title}</p>
                      {snag?.user_profiles?.full_name && (
                        <p className="text-xs text-gray-500 mt-0.5">Raised by: {snag?.user_profiles?.full_name}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 ml-4 flex-shrink-0">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${PRIORITY_COLORS?.[snag?.priority] || 'bg-gray-100 text-gray-700'}`}>
                        {snag?.priority || 'medium'}
                      </span>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS?.[snag?.status] || 'bg-gray-100 text-gray-700'}`}>
                        {snag?.status || 'open'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Incidents & Near Misses Section */}
        <div className="bg-white border border-gray-200 rounded-lg p-5 mb-4">
          <ReportSectionHeader title="Incidents & Near Misses" icon="AlertTriangle" count={allIncidents?.length || 0} />
          {!allIncidents?.length ? (
            <EmptySection message="No incidents or near misses logged on this date." />
          ) : (
            <div className="space-y-3">
              {(incidents || [])?.map((incident) => (
                <div key={incident?.id} className="py-2 border-b border-gray-100 last:border-0">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-medium text-gray-700 uppercase tracking-wide">Incident</span>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${PRIORITY_COLORS?.[incident?.severity_level] || 'bg-gray-100 text-gray-700'}`}>
                          {incident?.severity_level || 'unknown'}
                        </span>
                      </div>
                      <p className="text-sm font-medium text-gray-900 capitalize">{incident?.incident_type?.replace(/_/g, ' ') || '—'}</p>
                      {incident?.description && (
                        <p className="text-xs text-gray-500 mt-1 line-clamp-2">{incident?.description}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              {(nearMisses || [])?.map((nm) => (
                <div key={nm?.id} className="py-2 border-b border-gray-100 last:border-0">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-medium text-gray-700 uppercase tracking-wide">Near Miss</span>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          nm?.risk_level === 'High' ? 'bg-red-100 text-red-700' :
                          nm?.risk_level === 'Medium' ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700'
                        }`}>
                          {nm?.risk_level || 'unknown'}
                        </span>
                      </div>
                      {nm?.description && (
                        <p className="text-xs text-gray-500 mt-1 line-clamp-2">{nm?.description}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Documents Section */}
        {showDocuments && (
          <div className="bg-white border border-gray-200 rounded-lg p-5 mb-4">
            <ReportSectionHeader title="Document Activity" icon="FileText" count={documents?.length || 0} />
            {!documents?.length ? (
              <EmptySection message="No document activity on this date." />
            ) : (
              <div className="space-y-2">
                {documents?.map((doc) => (
                  <div key={doc?.id} className="flex items-start justify-between py-2 border-b border-gray-100 last:border-0">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{doc?.title || doc?.name || 'Untitled'}</p>
                      {doc?.category && (
                        <p className="text-xs text-gray-500 mt-0.5">{doc?.category}</p>
                      )}
                      {doc?._reason === 'expiring' && (
                        <p className="text-xs text-yellow-600 mt-0.5">Expires: {formatDate(doc?.expiry_date)}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 ml-4 flex-shrink-0">
                      {doc?._reason === 'expiring' ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
                          Expiring
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                          Uploaded
                        </span>
                      )}
                      {doc?.status && (
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS?.[doc?.status] || 'bg-gray-100 text-gray-700'}`}>
                          {doc?.status}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Site Diary Section */}
        <div className="bg-white border border-gray-200 rounded-lg p-5 mb-4">
          <ReportSectionHeader title="Site Diary" icon="BookOpen" count={diary ? 1 : 0} />
          {!diary ? (
            <EmptySection message="No diary entry for this date." />
          ) : (
            <div className="space-y-3">
              {diary?.weather && (
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Weather</p>
                  <p className="text-sm text-gray-900">{diary?.weather}</p>
                </div>
              )}
              {diary?.labour_on_site !== null && diary?.labour_on_site !== undefined && (
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Workforce on Site</p>
                  <p className="text-sm text-gray-900">{diary?.labour_on_site}</p>
                </div>
              )}
              {diary?.work_completed && (
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Work Completed</p>
                  <p className="text-sm text-gray-900 whitespace-pre-wrap">{diary?.work_completed}</p>
                </div>
              )}
              {diary?.deliveries && (
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Deliveries</p>
                  <p className="text-sm text-gray-900 whitespace-pre-wrap">{diary?.deliveries}</p>
                </div>
              )}
              {diary?.issues_delays && (
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Issues / Delays</p>
                  <p className="text-sm text-gray-900 whitespace-pre-wrap">{diary?.issues_delays}</p>
                </div>
              )}
              {diary?.general_notes && (
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">General Notes</p>
                  <p className="text-sm text-gray-900 whitespace-pre-wrap">{diary?.general_notes}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>{/* end #daily-site-report-content */}
    </div>
  );
};

export default ReportView;
